import React from "react";
import { useAuth } from "../context/AuthContext";
import { Phone, MapPin } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { LandingPageFooter } from "./LandingPageFooter";
import logo from "../assets/images/healthmatch-logo-primary.svg";

interface LandingPageLayoutProps {
  children: React.ReactNode;
  title: string;
  description?: string;
  logoUrl?: string;
  partnerLogoUrl?: string;
  partnerName?: string;
}

/**
 * Layout component for landing pages
 * Provides a simplified structure without main navigation
 */
export function LandingPageLayout({
  children,
  title,
  description = "Find your perfect clinical trial match with HealthMatch",
  logoUrl = logo,
  partnerLogoUrl,
  partnerName,
}: LandingPageLayoutProps) {
  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
      </Helmet>

      {/* Simple header with logo(s) */}
      <header className="relative z-50 py-3 px-4 sm:py-4 sm:px-6 lg:px-8 border-b border-gray-100">
        <div className="max-w-7xl mx-auto flex items-center justify-center sm:justify-between">
          <div className="flex items-center space-x-4">
            <div className="block">
              <img src={logoUrl} alt="HealthMatch" className="h-4 sm:h-5" />
            </div>

            {/* Partner logo if provided */}
            {partnerLogoUrl && partnerName && (
              <>
                <div className="h-6 w-px bg-gray-200"></div>
                <img
                  src={partnerLogoUrl}
                  alt={partnerName}
                  className="h-5 sm:h-7 max-w-[100px] sm:max-w-[120px] object-contain"
                />
              </>
            )}
          </div>
          <UserBadge />
        </div>
      </header>

      {/* Main content */}
      <main className="flex-grow">{children}</main>

      {/* Simple footer */}
      <LandingPageFooter />
    </div>
  );
}

function UserBadge() {
  const { isLoggedIn, user } = useAuth();
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  if (!isLoggedIn || !user) return null;

  const name = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email || user.id;
  const initials = (user.firstName?.[0] || "?") + (user.lastName?.[0] || "");
  const location = [user.suburb, user.postalCode].filter(Boolean).join(" ");

  return (
    <div ref={containerRef} className="relative hidden sm:flex items-center text-sm text-gray-700">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 shadow-sm hover:bg-gray-50"
      >
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-white text-xs font-semibold">
          {initials.toUpperCase()}
        </span>
        <span className="font-medium text-gray-800 max-w-[160px] truncate">{name}</span>
        <svg className="ml-1 h-3 w-3 text-gray-500" viewBox="0 0 20 20" fill="currentColor"><path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"/></svg>
      </button>

      {open && (
        <div className="absolute right-0 top-10 z-50 w-80 rounded-xl border border-gray-200 bg-white/95 backdrop-blur shadow-lg shadow-primary-900/10">
          {/* Header */}
          <div className="flex items-center gap-3 p-4 border-b">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-600 text-white font-semibold">
              {initials.toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-gray-900 truncate">{name}</div>
              {user.email && (
                <div className="flex items-center gap-2 text-xs text-gray-600 truncate">
                  <span className="truncate">{user.email}</span>
                </div>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="p-4 space-y-2 text-sm">
            {user.phoneNumber && (
              <div className="flex items-center gap-2 text-gray-700">
                <Phone className="h-4 w-4 text-gray-400" />
                <span className="truncate">{user.phoneNumber}</span>
              </div>
            )}
            {location && (
              <div className="flex items-center gap-2 text-gray-700">
                <MapPin className="h-4 w-4 text-gray-400" />
                <span className="truncate">{location}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
